/**
 * Engine-stable transcendentals + precomputed per-step constants (FS-02 §4.7).
 *
 * `Math.sin`/`cos`/`exp` are not bit-exact across engines, so any transcendental whose result
 * becomes simulation state uses this module. For the fixed timestep, damping collapses to one
 * module constant so the per-step flight model does no transcendental work at all.
 */
import { DAMPING, FIXED_DT } from './constants';

/** exp(-DAMPING * FIXED_DT), computed once at load (FS-02 §4.7). ≈ 0.9472914. */
export const DAMPING_PER_STEP = Math.exp(-DAMPING * FIXED_DT);

// 4096-entry quarter-wave table + linear interpolation, generated at load (deterministic per engine).
const TABLE_BITS = 12;
const TABLE_SIZE = 1 << TABLE_BITS; // 4096
const TAU = Math.PI * 2;
const SIN_TABLE = new Float32Array(TABLE_SIZE + 1);
for (let i = 0; i <= TABLE_SIZE; i++) {
  SIN_TABLE[i] = Math.sin((i / TABLE_SIZE) * TAU);
}

/** Deterministic sine via table + lerp. Domain-safe for all finite inputs. */
export function dsin(x: number): number {
  let phase = (x / TAU) % 1;
  if (phase < 0) phase += 1;
  const f = phase * TABLE_SIZE;
  const i = f | 0;
  const frac = f - i;
  const a = SIN_TABLE[i]!;
  const b = SIN_TABLE[i + 1]!;
  return a + (b - a) * frac;
}

export function dcos(x: number): number {
  return dsin(x + Math.PI / 2);
}

/** sqrt is correctly rounded per IEEE-754 and therefore safe. */
export const dsqrt = Math.sqrt;

export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Smoothstep ease used for hazard intensity → amplitude (FS-06 §4.14). */
export function smoothstep01(t: number): number {
  const c = clamp(t, 0, 1);
  return c * c * (3 - 2 * c);
}

export const TAU_CONST = TAU;

/** Wrap an angle into [0, 2π). */
export function wrap2pi(a: number): number {
  let r = a % TAU;
  if (r < 0) r += TAU;
  return r;
}

/** Signed smallest angular delta a→b, in (-π, π]. */
export function angularDelta(a: number, b: number): number {
  let d = (b - a) % TAU;
  if (d < -Math.PI) d += TAU;
  if (d > Math.PI) d -= TAU;
  return d;
}
